import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * StreakClaim — floating daily streak pill on the feed. Claim once a day
 * for a waves bonus (5 → 50 waves ladder, resets if you skip a day).
 * Arriving with ?claim=streak (from the Comeback Bonus push) claims in one tap.
 */
export default function StreakClaim() {
  const [status, setStatus] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [params, setParams] = useSearchParams();
  const autoRan = useRef(false);

  useEffect(() => {
    api.get("/streak").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const claim = useCallback(async (comeback = false) => {
    if (claiming) return;
    setClaiming(true);
    try {
      const { data } = await api.post("/streak/claim");
      toast.success(
        comeback
          ? `🔥 Comeback bonus! Day ${data.streak} saved — +${data.reward} waves. Balance: ${data.wave_balance}`
          : `🔥 Day ${data.streak} streak — +${data.reward} waves! Balance: ${data.wave_balance}`,
      );
      setStatus((s) => ({ ...s, claimed_today: true, streak: data.streak }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim streak");
    } finally {
      setClaiming(false);
    }
  }, [claiming]);

  useEffect(() => {
    if (!status || autoRan.current || params.get("claim") !== "streak") return;
    autoRan.current = true;
    const next = new URLSearchParams(params);
    next.delete("claim");
    setParams(next, { replace: true });
    if (!status.claimed_today) claim(true);
  }, [status, params, setParams, claim]);

  if (!status || status.claimed_today) return null;

  return (
    <button
      onClick={() => claim(false)}
      data-testid="streak-claim-pill"
      className="fixed bottom-28 right-4 z-30 inline-flex items-center gap-1.5 rounded-full bg-jam-gold text-jam-black pl-3 pr-4 py-2.5 shadow-xl shadow-jam-gold/20 hover:brightness-110 transition-all animate-fade-up"
    >
      <Fire size={16} weight="fill" className="animate-pulse" />
      <span className="text-[10px] font-black tracking-[0.15em] uppercase">
        Day {status.next_streak} · Claim {status.next_reward} waves
      </span>
    </button>
  );
}
